import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  InputAdornment,
  IconButton,
  MenuItem,
  Typography,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
} from "@mui/material";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { getUniqueCurrencies } from "../../utils/currencyOptions";

const getCurrencyAmount = (amountMap, currencyDetails) => {
  if (!amountMap || typeof amountMap !== "object") return 0;

  const candidates = [
    currencyDetails?.title,
    currencyDetails?.symbol,
    currencyDetails?.code,
    currencyDetails?.name,
  ]
    .filter(Boolean)
    .flatMap((value) => {
      const text = String(value).trim();
      return [text, text.toUpperCase(), text.toLowerCase()];
    });

  for (const key of candidates) {
    if (Object.prototype.hasOwnProperty.call(amountMap, key)) {
      const value = amountMap[key];
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }
  }

  const firstValue = Object.values(amountMap)[0];
  const parsed = Number(firstValue);
  return Number.isFinite(parsed) ? parsed : 0;
};

const resolveAmountValue = (value, currencyDetails) => {
  if (value && typeof value === "object") {
    return getCurrencyAmount(value, currencyDetails);
  }
  return parseMoney(value);
};

const addAmountMap = (target, source) => {
  Object.entries(source || {}).forEach(([currency, amount]) => {
    const parsed = Number(amount);
    if (!Number.isFinite(parsed)) return;
    target[currency] = (target[currency] || 0) + parsed;
  });
  return target;
};

const parseMoney = (value) =>
  Number(String(value ?? "0").replace(/,/g, "")) || 0;

const formatBillDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB");
};

const getOptionTitle = (options, value) => {
  const selected = (options || []).find((option) => String(option.id) === String(value));
  return selected?.title || selected?.name || value || "-";
};

const getPurchasePaidMap = (purchase) =>
  purchase?.total_paid_raw || purchase?.total_paid || {};

const buildSupplierCurrencySummary = (purchases) => {
  const summaryMap = {};

  purchases.forEach((purchase) => {
    const currency =
      purchase?.currency_details?.title ||
      purchase?.currency_title ||
      purchase?.currency ||
      "Unknown";
    if (!summaryMap[currency]) {
      summaryMap[currency] = {
        currency,
        totalAmount: 0,
        totalPaid: 0,
        totalRemained: 0,
      };
    }

    summaryMap[currency].totalAmount += getCurrencyAmount(
      purchase.total_price,
      purchase.currency_details
    );
    summaryMap[currency].totalPaid += getCurrencyAmount(
      getPurchasePaidMap(purchase),
      purchase.currency_details
    );
    summaryMap[currency].totalRemained += getCurrencyAmount(
      purchase.total_remained_amount,
      purchase.currency_details
    );
  });

  return Object.values(summaryMap).sort((a, b) => a.currency.localeCompare(b.currency));
};

const PurchasePaymentAdd = ({
  open,
  onClose,
  token,
  initialSupplierId = "",
  initialSupplierName = "",
  initialSupplierContact = "",
  initialPurchaseId = "",
  summary,
  billRows = [],
  onSuccess,
  paymentRecords = [],
  onEditPayment,
  onDeletePayment,
}) => {
  const { t } = useTranslation();
  const [editingRecord, setEditingRecord] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [editCurrencyId, setEditCurrencyId] = useState("");
  const [editAccountId, setEditAccountId] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [paymentStatuses, setPaymentStatuses] = useState({});

  const handleEditClick = (record) => {
    setEditingRecord(record);
    setEditAmount(String(record.amount || ""));
    setEditNotes(record.notes === "-" ? "" : record.notes || "");
    // Find currency id by title from currencyList
    const matchedCurrency = currencyList.find((c) => c.title === record.currency);
    setEditCurrencyId(matchedCurrency ? String(matchedCurrency.id) : "");
    // Find account id by name from shopAccounts
    const matchedAccount = shopAccounts.find((a) => a.name === record.account);
    setEditAccountId(matchedAccount ? String(matchedAccount.id) : "");
  };

  const handleSaveEdit = async () => {
    if (!editingRecord) return;
    setEditLoading(true);
    try {
      const BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api";
      const endpoint = `${BASE}/shop/account-transactions/${editingRecord.id}/`;
      const payload = {
        amount: editAmount,
        ...(editCurrencyId && { transaction_currency: editCurrencyId }),
        ...(editAccountId && { account: editAccountId }),
        description: editNotes,
      };
      await axios.patch(endpoint, payload, {
        headers: { Authorization: `Token ${token}` },
      });
      setEditingRecord(null);
    } catch (err) {
      console.error("Failed to update payment:", err);
    }
    setEditLoading(false);
  };
  const { handleAddAsync, loading, resetForm } = useAdd(
    "shop/purchase-payments",
    token
  );
  const {
    handleAddAsync: handleAddSupplierTransactionAsync,
    loading: supplierTransactionLoading,
  } = useAdd("shop/account-transactions", token);
  const {
    handleAddAsync: handleAddCurrencyAsync,
    loading: currencyCreateLoading,
  } = useAdd("shop/currencies", token);

  const [selectedShop, setSelectedShop] = useState("");

  const { data: suppliersData } = useFetchObjects(
    ["supplier", "purchase-payment-add"],
    "shop/suppliers/?page_size=all",
    token
  );
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currency", "purchase-payment-add"],
    "shop/currencies/?page_size=all",
    token
  );
  const { data: unitsData } = useFetchObjects(
    ["unit", "purchase-payment-add"],
    "shop/units/?page_size=all",
    token
  );
  const { data: purchasesData } = useFetchObjects(
    ["purchase", "purchase-payment-add"],
    "shop/purchases/?page_size=all",
    token
  );

  const currencies = getUniqueCurrencies(
    Array.isArray(currenciesData?.results)
      ? currenciesData.results
      : Array.isArray(currenciesData)
      ? currenciesData
      : []
  );
  const units = Array.isArray(unitsData?.results)
    ? unitsData.results
    : Array.isArray(unitsData)
    ? unitsData
    : [];
  const purchases = Array.isArray(purchasesData?.results) ? purchasesData.results : (Array.isArray(purchasesData) ? purchasesData : []);
  const suppliers = Array.isArray(suppliersData?.results)
    ? suppliersData.results
    : Array.isArray(suppliersData)
    ? suppliersData
    : [];
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [amount, setAmount] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");
  const [currencyDetails, setCurrencyDetails] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [, setTotalAmount] = useState(0);
  const [, setPaidAmount] = useState(0);
  const [, setRemainedAmount] = useState(0);
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().slice(0, 16)
  ); // Format for datetime-local
  const [notes, setNotes] = useState("");
  const [openCurrencyDialog, setOpenCurrencyDialog] = useState(false);
  const [newCurrencyTitle, setNewCurrencyTitle] = useState("");
  const [submitError, setSubmitError] = useState("");
  const supplierPaymentMode = Boolean(
    initialSupplierId || initialSupplierName || initialSupplierContact
  );

  const resolveInitialSupplier = () => {
    if (!initialSupplierId && !initialSupplierName && !initialSupplierContact) {
      return null;
    }

    return (
      suppliers.find((supplier) => String(supplier?.id) === String(initialSupplierId)) ||
      suppliers.find(
        (supplier) =>
          String(supplier?.name || "").trim().toLowerCase() ===
          String(initialSupplierName || "").trim().toLowerCase()
      ) ||
      suppliers.find(
        (supplier) =>
          String(supplier?.contact || "").trim().toLowerCase() ===
          String(initialSupplierContact || "").trim().toLowerCase()
      ) ||
      null
    );
  };

  useEffect(() => {
    if (!open) return;

    if (supplierPaymentMode) {
      const resolvedSupplier = resolveInitialSupplier();
      if (resolvedSupplier?.id) {
        setSelectedSupplier(String(resolvedSupplier.id));
      } else if (initialSupplierId) {
        setSelectedSupplier(String(initialSupplierId));
      }

      setSelectedPurchase(null);
      setSelectedShop("");
      setCurrencyDetails(null);
      setSelectedCurrency((current) => current || (currencies[0]?.id ? String(currencies[0].id) : ""));
      return;
    }

    if (initialPurchaseId) {
      const directPurchase = purchases.find(
        (purchase) => String(purchase?.id) === String(initialPurchaseId)
      );
      if (directPurchase) {
        applyPurchaseSelection(directPurchase);
        return;
      }
    }

    const resolvedSupplier = resolveInitialSupplier();
    if (resolvedSupplier?.id) {
      handleSupplierChange(String(resolvedSupplier.id));
      return;
    }

    if (initialSupplierId) {
      handleSupplierChange(String(initialSupplierId));
    }
    // Prefill the form from the supplier record when opened from Finance Manager.
  }, [
    open,
    supplierPaymentMode,
    initialSupplierId,
    initialSupplierName,
    initialSupplierContact,
    purchases,
    suppliers,
    currencies,
  ]);

  const supplierPurchases = purchases.filter(
    (purchase) => String(purchase?.supplier) === String(selectedSupplier)
  );
  const supplierCurrencySummary = buildSupplierCurrencySummary(supplierPurchases);
  const summaryCurrencyRows = useMemo(() => {
    const sourceRows =
      Array.isArray(summary?.currencyBreakdown) && summary.currencyBreakdown.length
        ? summary.currencyBreakdown
        : supplierCurrencySummary;

    return sourceRows
      .map((row) => ({
        currency: String(row?.currency || "-"),
        totalAmount: parseMoney(row?.totalAmount),
        totalPaid: parseMoney(row?.totalPaid),
        totalRemaining: parseMoney(row?.totalRemaining ?? row?.totalRemained),
      }))
      .filter((row) => row.currency !== "-");
  }, [summary, supplierCurrencySummary]);
  const selectedSupplierRecord = suppliers.find(
    (supplier) => String(supplier?.id) === String(selectedSupplier)
  );
  const normalizeLookupText = (value) =>
    String(value ?? "")
      .trim()
      .toLowerCase();

  const getPurchaseForSupplierCurrency = (supplierId, currencyId) => {
    const supplierIdText = normalizeLookupText(supplierId);
    const supplierNameText = normalizeLookupText(initialSupplierName);
    const supplierContactText = normalizeLookupText(initialSupplierContact);
    const targetCurrencyText = normalizeLookupText(
      currencies.find((currency) => String(currency.id) === String(currencyId))?.title ||
        currencyId
    );
    const billReferences = [
      billRows?.[0]?.billNo,
      billRows?.[0]?.id,
      summary?.billNo,
      summary?.billId,
    ]
      .filter(Boolean)
      .map((value) => normalizeLookupText(value));

    const supplierMatches = purchases.filter((purchase) => {
      const purchaseSupplierId = normalizeLookupText(purchase?.supplier);
      const purchaseSupplierName = normalizeLookupText(
        purchase?.supplier_details?.name ||
          purchase?.purchase_info?.supplier ||
          purchase?.purchase_info?.supplier_name
      );
      const purchaseSupplierContact = normalizeLookupText(
        purchase?.supplier_details?.contact ||
          purchase?.purchase_info?.supplier_contact
      );
      const purchaseBillRefs = [
        purchase?.billNo,
        purchase?.bill_no,
        purchase?.purchase_no,
        purchase?.purchase_number,
        purchase?.purchase_info?.billNo,
        purchase?.purchase_info?.bill_no,
      ]
        .filter(Boolean)
        .map((value) => normalizeLookupText(value));

      return (
        (supplierIdText && purchaseSupplierId === supplierIdText) ||
        (supplierNameText && purchaseSupplierName === supplierNameText) ||
        (supplierContactText && purchaseSupplierContact === supplierContactText) ||
        purchaseBillRefs.some((ref) => billReferences.includes(ref))
      );
    });

    const currencyMatches = supplierMatches
      .filter((purchase) => {
        const purchaseCurrencyId = normalizeLookupText(
          purchase?.currency_details?.id || purchase?.currency
        );
        const purchaseCurrencyText = normalizeLookupText(
          purchase?.currency_details?.title ||
            purchase?.currency_title ||
            purchase?.currency_details?.code ||
            purchase?.currency
        );
        return (
          purchaseCurrencyId === normalizeLookupText(currencyId) ||
          (targetCurrencyText && purchaseCurrencyText === targetCurrencyText)
        );
      })
      .sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));

    return currencyMatches[0] || supplierMatches[0] || purchases[0] || null;
  };
  const getActiveSupplierPurchase = () =>
    selectedPurchase ||
    (supplierPaymentMode && selectedSupplier && selectedCurrency
      ? getPurchaseForSupplierCurrency(selectedSupplier, selectedCurrency)
      : null);
  const activeSupplierPurchase = useMemo(
    () =>
      supplierPaymentMode
        ? getPurchaseForSupplierCurrency(
            selectedSupplier || initialSupplierName || initialSupplierContact,
            selectedCurrency
          ) ||
          purchases.find((purchase) => String(purchase?.supplier) === String(selectedSupplier)) ||
          null
        : null,
    [
      supplierPaymentMode,
      selectedSupplier,
      initialSupplierName,
      initialSupplierContact,
      selectedCurrency,
      purchases,
      billRows,
      summary,
    ]
  );
  const handleQuickCurrencySave = async () => {
    const title = newCurrencyTitle.trim();
    if (!title) return;

    try {
      const createdCurrency = await handleAddCurrencyAsync({
        title,
        description: "",
      });
      if (createdCurrency?.id) {
        setSelectedCurrency(String(createdCurrency.id));
        setCurrencyDetails(createdCurrency);
      }
      setNewCurrencyTitle("");
      setOpenCurrencyDialog(false);
    } catch {
      // The hook already shows the backend error toast.
    }
  };
  const selectedCurrencyDetails = currencies.find(
    (currency) => String(currency.id) === String(selectedCurrency)
  );
  const selectedCurrencySummary = useMemo(() => {
    const target = String(
      selectedCurrencyDetails?.title ||
        selectedCurrencyDetails?.symbol ||
        selectedCurrencyDetails?.code ||
        selectedCurrencyDetails?.name ||
        ""
    )
      .trim()
      .toLowerCase();

    if (!target) return summaryCurrencyRows[0] || null;

    return (
      summaryCurrencyRows.find(
        (row) => String(row.currency || "").trim().toLowerCase() === target
      ) || null
    );
  }, [selectedCurrencyDetails, summaryCurrencyRows]);
  const selectedCurrencyRemaining = Number(selectedCurrencySummary?.totalRemaining || 0);
  const selectedCurrencyCode = String(
    selectedCurrencyDetails?.title ||
      selectedCurrencyDetails?.code ||
      selectedCurrencyDetails?.symbol ||
      selectedCurrencyDetails?.name ||
      ""
  )
    .trim()
    .toLowerCase();
  const selectedAccountCurrencyCode = String(
    selectedAccount?.currency_title ||
      selectedAccount?.currency_details?.title ||
      selectedAccount?.currency_details?.code ||
      selectedAccount?.currency_title ||
      ""
  )
    .trim()
    .toLowerCase();
  const displayCurrencyTitle =
    selectedPurchase?.currency_details?.title ||
    selectedCurrencyDetails?.title ||
    selectedPurchase?.currency_title ||
    selectedPurchase?.currency ||
    "";
  const selectedBillAmount = selectedPurchase
    ? resolveAmountValue(
        selectedPurchase.total_price ?? selectedPurchase.total,
        selectedPurchase.currency_details || selectedCurrencyDetails
      )
    : 0;
  const selectedBillPaid = selectedPurchase
    ? resolveAmountValue(
        selectedPurchase.total_paid_raw ?? selectedPurchase.total_paid ?? selectedPurchase.paid,
        selectedPurchase.currency_details || selectedCurrencyDetails
      )
    : 0;
  const selectedBillRemaining = selectedPurchase
    ? resolveAmountValue(
        selectedPurchase.total_remained_amount ??
          selectedPurchase.remaining ??
          Math.max(selectedBillAmount - selectedBillPaid, 0),
        selectedPurchase.currency_details || selectedCurrencyDetails
      )
    : 0;
  const formatBillAmountLine = (value, sign = "") => {
    const amount = Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${sign ? `${sign} ` : ""}${amount} ${displayCurrencyTitle || "-"}`;
  };
  const requiresExchangeRate =
    Boolean(selectedCurrencyCode) &&
    Boolean(selectedAccountCurrencyCode) &&
    selectedCurrencyCode !== selectedAccountCurrencyCode;

  const applyPurchaseSelection = (purchase) => {
    if (!purchase) {
      setSelectedPurchase(null);
      setSelectedShop("");
      setCurrencyDetails(null);
      setSelectedCurrency("");
      setTotalAmount(0);
      setPaidAmount(0);
      setRemainedAmount(0);
      setSelectedAccountId("");
      setSelectedAccount(null);
      return;
    }

    const purchaseCurrency = purchase.currency_details || null;
    setSelectedPurchase(purchase);
    setSelectedShop(
      purchase?.shop_details?.id
        ? String(purchase.shop_details.id)
        : purchase?.shop
        ? String(purchase.shop)
        : ""
    );
    setCurrencyDetails(purchaseCurrency);
    setSelectedCurrency(
      purchaseCurrency?.id
        ? String(purchaseCurrency.id)
        : purchase?.currency
        ? String(purchase.currency)
        : ""
    );
    setTotalAmount(getCurrencyAmount(purchase.total_price, purchaseCurrency));
    setPaidAmount(
      getCurrencyAmount(purchase.total_paid_raw || purchase.total_paid, purchaseCurrency)
    );
    setRemainedAmount(
      getCurrencyAmount(purchase.total_remained_amount, purchaseCurrency)
    );
    setSelectedAccountId("");
    setSelectedAccount(null);
  };

  const selectedShopId =
    selectedShop || selectedPurchase?.shop_details?.id || selectedPurchase?.shop || "";
  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["accounts", "purchase-payment-add"],
    "shop/accounts/?page_size=all",
    token
  );
  const accounts = Array.isArray(accountsData)
    ? accountsData
    : accountsData?.results || [];
  const shopAccounts = accounts;
  const sameCurrencyAccounts = accounts.filter(
    (acc) => String(acc.currency) === String(selectedCurrency)
  );

  const { data: directRateData } = useFetchObjects(
    [
      "purchase-payment-rate-direct",
      selectedShopId,
      currencyDetails?.id,
      selectedAccount?.currency,
    ],
    selectedShopId && currencyDetails?.id && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShopId}&from_currency=${currencyDetails.id}&to_currency=${selectedAccount.currency}`
      : "",
    token
  );

  const { data: reverseRateData } = useFetchObjects(
    [
      "purchase-payment-rate-reverse",
      selectedShopId,
      currencyDetails?.id,
      selectedAccount?.currency,
    ],
    selectedShopId && currencyDetails?.id && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShopId}&from_currency=${selectedAccount.currency}&to_currency=${currencyDetails.id}`
      : "",
    token
  );

  const handleSupplierChange = (supplierId) => {
    setSelectedSupplier(supplierId);

    if (supplierPaymentMode) {
      setSelectedPurchase(null);
      setSelectedShop("");
      setCurrencyDetails(null);
      return;
    }

    const supplierPurchaseList = purchases
      .filter((purchase) => {
        const purchaseSupplierId = normalizeLookupText(purchase?.supplier);
        const purchaseSupplierName = normalizeLookupText(
          purchase?.supplier_details?.name ||
            purchase?.purchase_info?.supplier ||
            purchase?.purchase_info?.supplier_name
        );
        const purchaseSupplierContact = normalizeLookupText(
          purchase?.supplier_details?.contact ||
            purchase?.purchase_info?.supplier_contact
        );

        return (
          purchaseSupplierId === normalizeLookupText(supplierId) ||
          purchaseSupplierName === normalizeLookupText(initialSupplierName) ||
          purchaseSupplierContact === normalizeLookupText(initialSupplierContact)
        );
      })
      .sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));

    const firstPurchase = supplierPurchaseList[0] || null;
    if (firstPurchase) {
      applyPurchaseSelection(firstPurchase);
      return;
    }

    applyPurchaseSelection(null);
  };

  useEffect(() => {
    if (!selectedAccountId) {
      setSelectedAccount(null);
      return;
    }
    const acc = accounts.find((a) => String(a.id) === String(selectedAccountId));
    setSelectedAccount(acc || null);
  }, [selectedAccountId, accounts]);

  useEffect(() => {
    if (!selectedCurrency) {
      setSelectedAccountId("");
      return;
    }
    if (shopAccounts.length === 0) {
      setSelectedAccountId("");
      return;
    }
    const exists = shopAccounts.some(
      (acc) => String(acc.id) === String(selectedAccountId)
    );
    if (!exists) {
      const preferred = sameCurrencyAccounts[0] || shopAccounts[0];
      setSelectedAccountId(preferred ? String(preferred.id) : "");
    }
  }, [selectedCurrency, selectedAccountId, shopAccounts, sameCurrencyAccounts]);

  const exchangeRatePreview = (() => {
    if (!requiresExchangeRate) return "1";
    if (!currencyDetails) return "";
    const accountCurrencyId = selectedAccount?.currency ?? currencyDetails.id;
    if (String(currencyDetails.id) === String(accountCurrencyId)) return "1";
    const direct = directRateData?.results?.[0]?.rate;
    if (direct) return String(direct);
    const reverse = reverseRateData?.results?.[0]?.rate;
    if (reverse && Number(reverse) > 0) return String(1 / Number(reverse));
    return "";
  })();

  const convertedAmountPreview = (() => {
    if (!requiresExchangeRate || !amount || !exchangeRate) return "";
    const nAmount = Number(amount);
    const nRate = Number(exchangeRate);
    if (!Number.isFinite(nAmount) || !Number.isFinite(nRate) || nAmount <= 0 || nRate <= 0) {
      return "";
    }
    return (nAmount * nRate).toFixed(2);
  })();

  useEffect(() => {
    setExchangeRate(exchangeRatePreview);
  }, [exchangeRatePreview]);

  // Keep typing free and validate on submit instead of blocking keystrokes.
  const handleAmountChange = (e) => {
    const value = e.target.value;
    const numericValue = Number(value);
    if (value === "" || !Number.isFinite(numericValue) || numericValue >= 0) {
      setAmount(value);
    }
  };

  const handleSubmit = async () => {
    setSubmitError("");

    if (amount === "" || Number(amount) <= 0) {
      setSubmitError("Please enter a valid amount.");
      return;
    }

    if (!selectedCurrency || !selectedAccountId) {
      setSubmitError("Please select a currency and account.");
      return;
    }

    if (requiresExchangeRate && !supplierPaymentMode && !exchangeRate) {
      setSubmitError("Please enter an exchange rate.");
      return;
    }

    if (!supplierPaymentMode && Number(amount) > selectedCurrencyRemaining) {
      setSubmitError("Amount cannot be greater than the remaining balance.");
      return;
    }

    if (supplierPaymentMode) {
      const paymentDescription = [
        `Supplier payment${initialSupplierName ? ` for ${initialSupplierName}` : ""}`,
        notes.trim(),
      ]
        .filter(Boolean)
        .join(" | ");

      try {
        await handleAddSupplierTransactionAsync({
          account: Number(selectedAccountId),
          transaction_currency: Number(selectedCurrency),
          transaction_type: "debit",
          amount: Number(amount),
          description: paymentDescription || `Supplier payment${initialSupplierName ? ` for ${initialSupplierName}` : ""}`,
          transaction_date: paymentDate.slice(0, 10),
        });
      } catch (error) {
        const backendErrors = error?.response?.data;
        if (backendErrors && typeof backendErrors === "object") {
          const message = Object.entries(backendErrors)
            .map(([key, value]) => {
              const field = key.charAt(0).toUpperCase() + key.slice(1);
              const detail = Array.isArray(value) ? value.join(", ") : value;
              return `${field}: ${detail}`;
            })
            .join(" | ");
          setSubmitError(message || "Failed to record supplier payment.");
        } else {
          setSubmitError(error?.message || "Failed to record supplier payment.");
        }
        return;
      }

      onSuccess?.({
        amount: Number(amount),
        supplierId: selectedSupplier,
        currencyId: selectedCurrency,
        accountId: selectedAccountId,
        notes: paymentDescription || notes,
      });

      setSelectedPurchase(null);
      setSelectedShop("");
      setSelectedSupplier("");
      setSelectedCurrency("");
      setAmount("");
      setExchangeRate("");
      setCurrencyDetails(null);
      setTotalAmount(0);
      setPaidAmount(0);
      setRemainedAmount(0);
      setPaymentDate(new Date().toISOString().slice(0, 16));
      setNotes("");
      setSelectedAccountId("");
      setSelectedAccount(null);
      setSubmitError("");
      onClose();
      return;
    } else {
      if (!selectedPurchase) {
        setSubmitError("Please select a purchase.");
        return;
      }

      const formData = new FormData();
      formData.append("purchase", selectedPurchase.id);
      formData.append("currency", selectedCurrency);
      if (selectedAccountId) formData.append("account", selectedAccountId);
      formData.append("amount", amount);
      formData.append("exchange_rate", exchangeRate);
      formData.append("payment_date", paymentDate); // Submit payment date
      formData.append("notes", notes);
      try {
        await handleAddAsync(formData);
      } catch (error) {
        const backendErrors = error?.response?.data;
        if (backendErrors && typeof backendErrors === "object") {
          const message = Object.entries(backendErrors)
            .map(([key, value]) => {
              const field = key.charAt(0).toUpperCase() + key.slice(1);
              const detail = Array.isArray(value) ? value.join(", ") : value;
              return `${field}: ${detail}`;
            })
            .join(" | ");
          setSubmitError(message || "Failed to record payment.");
        } else {
          setSubmitError(error?.message || "Failed to record payment.");
        }
        return;
      }
    }

    onSuccess?.({
      amount: Number(amount),
      supplierId: selectedSupplier,
      currencyId: selectedCurrency,
      billId: (activeSupplierPurchase || getActiveSupplierPurchase())?.id || "",
      billNo:
        (activeSupplierPurchase || getActiveSupplierPurchase())?.billNo ||
        (activeSupplierPurchase || getActiveSupplierPurchase())?.id ||
        "",
    });
  };

  // Reset form after successful addition
  useEffect(() => {
    if (resetForm) {
      setSelectedPurchase(null);
      setSelectedShop("");
      setSelectedSupplier("");
      setSelectedCurrency("");
      setAmount("");
      setExchangeRate("");
      setCurrencyDetails(null);
      setTotalAmount(0);
      setPaidAmount(0);
      setRemainedAmount(0);
      setPaymentDate(new Date().toISOString().slice(0, 16)); // Reset payment date
      setNotes("");
      setSelectedAccountId("");
      setSelectedAccount(null);
      setSubmitError("");
      onClose();
    }
  }, [resetForm, onClose]);

  const currencyList = currencies || [];
  const selectedPurchaseLabel = selectedPurchase
    ? `#${String(selectedPurchase?.billNo || selectedPurchase?.id || "").padStart(2, "0")}`
    : "";
  const selectedPurchaseSupplierName =
    selectedPurchase?.supplier_details?.name ||
    selectedSupplierRecord?.name ||
    initialSupplierName ||
    "-";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { width: "50%", maxWidth: "50%" } }}>
      <DialogTitle>{t("purchasePayment.addPayment")}</DialogTitle>
      <DialogContent>
          <Box component="form" noValidate sx={{ mt: 1 }}>
          {submitError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {submitError}
            </Alert>
          )}
          {supplierPaymentMode && (
            <Box
              sx={{
                mt: 1,
                mb: 1,
                p: 1.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "grey.50",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {t("purchasePayment.paymentDetails", "Payment Details")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedSupplierRecord?.name || initialSupplierName || "-"}
                </Typography>
              </Box>
              <Box sx={{ display: "grid", gap: 1 }}>
                {summaryCurrencyRows.length > 0 ? (
                  summaryCurrencyRows.map((row) => (
                    <Box
                      key={row.currency}
                      sx={{
                        p: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75 }}>
                        {row.currency}
                      </Typography>
                      <Box sx={{ display: "grid", gap: 0.5 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                            {t("purchasePayment.totalAmount", "Total Amount")}
                          </Typography>
                          <Typography sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                            {Number(row.totalAmount || 0).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                            {t("purchasePayment.totalPaid", "Total Paid")}
                          </Typography>
                          <Typography sx={{ fontWeight: 700, color: "success.main", whiteSpace: "nowrap" }}>
                            {Number(row.totalPaid || 0).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                            {t("purchasePayment.totalRemaining", "Total Remaining")}
                          </Typography>
                          <Typography sx={{ fontWeight: 700, color: "error.main", whiteSpace: "nowrap" }}>
                            {Number(row.totalRemaining || 0).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Alert severity="info">
                    {t("purchasePayment.noCurrenciesAvailable", "No currencies available")}
                  </Alert>
                )}
              </Box>
            </Box>
          )}

          {!supplierPaymentMode && selectedPurchase && (
            <Box
              sx={{
                mt: 1,
                mb: 1,
                p: 1.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "grey.50",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {selectedPurchaseLabel}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedPurchaseSupplierName}
                </Typography>
              </Box>
              <Box sx={{ display: "grid", gap: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                  <Typography sx={{ fontWeight: 700 }}>Total:</Typography>
                  <Typography sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    {formatBillAmountLine(selectedBillAmount)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                  <Typography sx={{ fontWeight: 700 }}>Paid:</Typography>
                  <Typography sx={{ fontWeight: 700, color: "success.main", whiteSpace: "nowrap" }}>
                    {formatBillAmountLine(selectedBillPaid, "+")}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                  <Typography sx={{ fontWeight: 700, color: "error.main" }}>Remaining:</Typography>
                  <Typography sx={{ fontWeight: 700, color: "error.main", whiteSpace: "nowrap" }}>
                    {formatBillAmountLine(selectedBillRemaining, "-")}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Currency Selection (Disabled, as it's derived from the purchase) */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
          <TextField
            select
            margin="dense"
            label={t("purchasePayment.selectCurrency")}
            fullWidth
            required
            value={selectedCurrency}
            disabled={currencyLoading}
            sx={{
              "& .MuiSelect-select": {
                pr: supplierPaymentMode ? 10 : 4,
              },
            }}
            onChange={(e) => {
              const nextCurrencyId = e.target.value;
              setSelectedCurrency(nextCurrencyId);
              const selected = currencyList.find(
                (c) => String(c.id) === String(nextCurrencyId)
              );
              setCurrencyDetails(selected || null);
              const matchingPurchase =
                selectedSupplier && nextCurrencyId
                  ? getPurchaseForSupplierCurrency(selectedSupplier, nextCurrencyId)
                  : null;
              if (matchingPurchase) {
                applyPurchaseSelection(matchingPurchase);
              } else if (!supplierPaymentMode) {
                setSelectedPurchase(null);
              }
            }}
            InputProps={{
              endAdornment: supplierPaymentMode ? (
                <InputAdornment position="end" sx={{ mr: 1 }}>
                  <IconButton
                    size="small"
                    edge="end"
                    color="success"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setOpenCurrencyDialog(true);
                    }}
                    aria-label={t("currency.addCurrency", "Add Currency")}
                  >
                    <AddCircleOutlineOutlinedIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          >
            {currencyLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              currencyList.map((curr) => (
                <MenuItem key={curr.id} value={String(curr.id)}>
                  {curr.title}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            select
            margin="dense"
            label={t("purchasePayment.account", "Account")}
            fullWidth
            required
            disabled={!selectedCurrency || accountLoading}
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value)}
          >
            {accountLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : shopAccounts.length === 0 ? (
              <MenuItem value="" disabled>
                {t("purchasePayment.noAccount", "No accounts found")}
              </MenuItem>
            ) : (
              shopAccounts.map((acc) => (
                <MenuItem key={acc.id} value={String(acc.id)}>
                  {acc.name}
                </MenuItem>
              ))
            )}
          </TextField>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
          <TextField
            margin="dense"
            label={`${t("purchasePayment.amount")} ${displayCurrencyTitle ? `(${displayCurrencyTitle})` : ""}`.trim()}
            type="number"
            fullWidth
            required
            value={amount}
            onChange={handleAmountChange}
            inputProps={{
              min: 0,
              step: "any",
            }}
            helperText={
              selectedCurrencyRemaining > 0 && amount !== "" && Number(amount) > selectedCurrencyRemaining
                ? "This will be recorded as an advance payment for the supplier."
                : ""
            }
          />

          {requiresExchangeRate && !supplierPaymentMode && (
            <>

              <TextField
                margin="dense"
                label={t("purchasePayment.exchangeRate", "Exchange Rate")}
                type="number"
                fullWidth
                required
                value={exchangeRate}
                onChange={(e) => setExchangeRate(e.target.value)}
                inputProps={{ min: 0, step: "0.000001" }}
              />

              {!exchangeRatePreview && selectedPurchase && selectedAccount && !exchangeRate && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {t("purchasePayment.missingRate", "No exchange rate found for selected account currency. You can enter it manually.")}
                </Alert>
              )}

              <TextField
                margin="dense"
                label={t("purchasePayment.convertedAmount", "Converted Amount")}
                fullWidth
                value={convertedAmountPreview}
                disabled
              />
            </>
          )}

          {/* Payment Date Input */}
          <TextField
            margin="dense"
            label={t("purchasePayment.paymentDate")}
            type="datetime-local"
            fullWidth
            required
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          </Box>

          <TextField
            margin="dense"
            label={t("purchasePayment.notes")}
            multiline
            rows={3}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          {/* Payment History */}
          {paymentRecords.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "text.secondary" }}>
                {t("agent.paymentHistory", "Payment History")}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "primary.main" }}>
                      <TableCell sx={{ color: "white" }}>{t("agent.reference", "Ref")}</TableCell>
                      <TableCell sx={{ color: "white" }}>{t("agent.amount", "Amount")}</TableCell>
                      <TableCell sx={{ color: "white" }}>{t("agent.currency", "Currency")}</TableCell>
                      <TableCell sx={{ color: "white" }}>{t("agent.account", "Account")}</TableCell>
                      <TableCell sx={{ color: "white" }}>{t("agent.paymentDate", "Date")}</TableCell>
                      <TableCell sx={{ color: "white" }}>{t("agent.notes", "Notes")}</TableCell>
                      <TableCell sx={{ color: "white" }}>{t("agent.status", "Status")}</TableCell>
                      <TableCell sx={{ color: "white" }}>{t("agent.action", "Action")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentRecords.map((record) => {
                      const isEditing = editingRecord?.id === record.id && editingRecord?.recordType === record.recordType;
                      return (
                        <TableRow key={`${record.recordType}-${record.reference}`} hover>
                          <TableCell>{record.reference}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "success.main" }}>
                            {isEditing ? (
                              <TextField type="number" value={editAmount} onChange={(e) => setEditAmount(e.target.value)}
                                size="small" variant="standard" sx={{ width: 90 }} />
                            ) : (
                              Number(record.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                            )}
                          </TableCell>
                          <TableCell>
                            {isEditing ? (
                              <TextField
                                select
                                value={editCurrencyId}
                                onChange={(e) => setEditCurrencyId(e.target.value)}
                                size="small"
                                variant="standard"
                                sx={{ minWidth: 80 }}
                              >
                                {currencyList.map((c) => (
                                  <MenuItem key={c.id} value={String(c.id)}>{c.title}</MenuItem>
                                ))}
                              </TextField>
                            ) : (
                              record.currency
                            )}
                          </TableCell>
                          <TableCell>
                            {isEditing ? (
                              <TextField
                                select
                                value={editAccountId}
                                onChange={(e) => setEditAccountId(e.target.value)}
                                size="small"
                                variant="standard"
                                sx={{ minWidth: 100 }}
                              >
                                {shopAccounts.map((a) => (
                                  <MenuItem key={a.id} value={String(a.id)}>{a.name}</MenuItem>
                                ))}
                              </TextField>
                            ) : (
                              record.account
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>
                            {record.paymentDate ? new Date(record.paymentDate).toLocaleString() : "-"}
                          </TableCell>
                          <TableCell>
                            {isEditing ? (
                              <TextField value={editNotes} onChange={(e) => setEditNotes(e.target.value)}
                                size="small" variant="standard" sx={{ width: 130 }} />
                            ) : (
                              record.notes
                            )}
                          </TableCell>
                          {/* Status checkbox — once checked, cannot be unchecked */}
                          <TableCell>
                            <Checkbox
                              size="small"
                              checked={Boolean(paymentStatuses[`${record.recordType}-${record.id}`])}
                              onChange={() => {
                                const key = `${record.recordType}-${record.id}`;
                                if (paymentStatuses[key]) return; // locked once checked
                                setPaymentStatuses((prev) => ({ ...prev, [key]: true }));
                              }}
                              disabled={Boolean(paymentStatuses[`${record.recordType}-${record.id}`])}
                              sx={{ p: 0, color: "success.main", "&.Mui-checked": { color: "success.main" } }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              {isEditing ? (
                                <>
                                  <Button size="small" variant="contained" color="success" disabled={editLoading}
                                    onClick={handleSaveEdit} sx={{ minWidth: 48, fontSize: 11, px: 1, py: 0.3 }}>
                                    {t("agent.save", "Save")}
                                  </Button>
                                  <Button size="small" color="secondary" disabled={editLoading}
                                    onClick={() => setEditingRecord(null)} sx={{ minWidth: 48, fontSize: 11, px: 1, py: 0.3 }}>
                                    {t("agent.cancel", "Cancel")}
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <IconButton size="small" color="success" onClick={() => handleEditClick(record)}>
                                    <EditOutlinedIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" color="error" onClick={() => onDeletePayment?.(record)}>
                                    <DeleteOutlineOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("purchasePayment.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
        disabled={
          loading ||
          supplierTransactionLoading ||
          !selectedCurrency ||
          !selectedAccountId ||
          !amount ||
          (requiresExchangeRate && !supplierPaymentMode && !exchangeRate)
        }
        >
          {loading || supplierTransactionLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("purchasePayment.submit")
          )}
        </Button>
      </DialogActions>

      <Dialog
        open={openCurrencyDialog}
        onClose={() => {
          if (currencyCreateLoading) return;
          setOpenCurrencyDialog(false);
          setNewCurrencyTitle("");
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t("currency.addCurrency", "Add Currency")}</DialogTitle>
        <DialogContent>
          <Box component="form" noValidate sx={{ mt: 1 }}>
            <TextField
              autoFocus
              margin="dense"
              label={t("currency.title", "Title")}
              fullWidth
              required
              value={newCurrencyTitle}
              onChange={(e) => setNewCurrencyTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleQuickCurrencySave();
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (currencyCreateLoading) return;
              setOpenCurrencyDialog(false);
              setNewCurrencyTitle("");
            }}
            color="secondary"
            disabled={currencyCreateLoading}
          >
            {t("currency.cancel", "Cancel")}
          </Button>
          <Button
            onClick={handleQuickCurrencySave}
            variant="contained"
            color="primary"
            disabled={currencyCreateLoading || !newCurrencyTitle.trim()}
          >
            {currencyCreateLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t("currency.submit", "Save")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default PurchasePaymentAdd;
